import {Component} from '@angular/core';
import {FormBuilder, FormGroup, Validators} from "@angular/forms";
import {AuthService} from "../shared/auth.service";
import {User, UserStatus} from "../interfaces/user";
import {ActivatedRoute, Router} from "@angular/router";
import {SubscribedComponent} from "../subscribed.component";
import {NbToastRef, NbToastrService} from "@nebular/theme";
import {TranslateService} from "@ngx-translate/core";
import {BSValidators} from "../shared/bsvalidators";
import {NotifierService} from "../shared/notifier.service";
import {Globals} from "../shared/Globals";

@Component({
    selector: 'bst-login',
    templateUrl: './login.component.html',
    styleUrls: ['./login.component.scss'],
    standalone: false
})
export class LoginComponent extends SubscribedComponent{
  public id:string = null;
  public form: FormGroup;
  public reset: FormGroup;
  public forgot: FormGroup;
  public path: string = '/auth';
  private wmevt;

  // An explicit ?redirect (OAuth round-trips one back through AuthController)
  // wins over the role-derived landing page. Null until that param sets it.
  protected redirectRoute: string = null;

  // A getter, not a field: every navigation below runs AFTER the login resolved,
  // and landingRoute needs the user that resolve produced. Resolving it at field
  // init would pin the anonymous answer for the whole component.
  protected get defaultRoute(): string {
    return this.redirectRoute ?? this.auth.landingRoute;
  }
  public email = null;
  public user: User;
  protected itoast: NbToastRef|null = null;
  protected submitting: boolean = false;
  win: Window;

  constructor(protected fb: FormBuilder,
              protected auth: AuthService,
              protected route: ActivatedRoute,
              protected toast: NbToastrService,
              protected notifier: NotifierService,
              protected translate: TranslateService,
              protected router: Router) {
    super();
  }

  ngOnDestroy() {
    super.ngOnDestroy();
    if(this.win){
      this.win.close();
      this.win = null;
    }

    if(this.wmevt){
      window.removeEventListener('message', this.wmevt, false);
      this.wmevt = null;
    }
  }

  ngOnInit(): void {
    super.ngOnInit();
    this.subs.push(this.route.queryParamMap.subscribe(async (next) => {

      this.form = null;
      this.reset = null;
      this.forgot = null;

      if(next.get('state') === 'logout'){
        await this.auth.logout();
      }
      else if(next.get('state') == 'verified'){
        const info = await this.translate.get(['emailverified', 'success']).toPromise();
        this.toast.success(info.emailverified, info.success, {
          icon: 'checkmark'
        });
      }
      else if(next.get('state') == 'reset'
        && next.has('token')
        && next.has('email')
        && next.has('with')){

        const email = next.get('email');
        const token = next.get('token');
        const key = next.get('with');

        this.reset = this.fb.group({
          key: [key, Validators.required],
          token: [token, [Validators.required]],
          email: [email, [Validators.required, BSValidators.email]],
          password: [null, [Validators.required]],
        }, {});

        return;
      }
      else if(next.get('state') === 'forgot'){
        this.forgot = this.fb.group({
          email: [next.get('email') || null, [Validators.required, BSValidators.email]]
        });

        return;
      }
      else if(next.get('state') === 'exp'){
        this.auth.clear();
        this.translate.get(['expired', 'pleaselogin']).toPromise().then((res) => {
          this.toast.warning(res.expired, res.pleaselogin, {
            icon: 'alert-triangle'
          });
        });
      }

      if(next.has('token') && window.opener){
        window.opener.postMessage(next.get('token'), document.location.origin);
        return;
      }

      if(next.has('email')){
        this.email = next.get('email');
      }

      if(next.has('error')){
        this.errorToast(next.get('error'));
      }

      if(next.has('redirect')){
        this.redirectRoute = '/' + next.get('redirect');
      }

      if(next.has('with')){
        // Must be responding to an invite
        await this.invitedWith(next.get('with'), next.has('error') ? next.get('error') : null);
      }
      else if(this.auth.loggedIn){
        if(next.has('token')){
          await this.loginToast();
        }
        await this.router.navigateByUrl(this.defaultRoute);
        return;
      }

      this.form = this.fb.group({
        with: [this.id],
        username: [{value: this.user ? this.user.username : this.email, disabled: this.user},
          [Validators.required, BSValidators.email]],
        password: [null, [Validators.required]]
      });
    }));
  }

  protected async entry(){
    await this.router.navigateByUrl(this.defaultRoute);
  }

  protected errorToast(error = null){
    this.toast.danger(error, this.translate.instant('error'), {
      duration: 10000
    });
  }

  async submit() {
    if(!this.submitting) {
      this.submitting = true;
      const formData = this.form.getRawValue();
      const did: User = await this.auth.login(formData.username, formData.password, formData.with);
      if (did) {
        if(this.itoast){
          this.itoast.close();
        }

        await this.loginToast();
        await this.router.navigateByUrl(this.defaultRoute);
      }
      this.submitting = false;
    }
  }

  private async loginToast(){
    const info = await this.translate.get(['loggedin', 'success']).toPromise();
    this.toast.info(info.loggedin, info.success, {
      duration: 3500,
      icon: 'log-in'
    });
  }

  async submitReset() {
    if(!this.submitting){
      this.submitting = true;
      const did: User = await this.auth.finishResetPassword(this.reset.getRawValue());
      if (did) {
        await this.loginToast();
        await this.router.navigateByUrl(this.defaultRoute);
      }


      this.submitting = false;
    }
  }

  socialLogin(provider: string){
    return this.listenWindow(Globals.popup(this.path + '/'+provider+'/login?with='+this.id, provider, 520, 570));
  }

  protected async invitedWith(withid: string, error = null){

    // clear any current login
    await this.auth.logout();

    // Invited User
    this.id = withid;
    let response: User;
    try {
      response = await this.auth.invitedUser(this.id);
    } catch (e) {
      // "Already active, log in instead" is a 400 whose data is the user; the other
      // 400s (inactive, no client, invalid invite) carry no data. errorify toasted
      // all of them; fall through to the plain form with whatever the body gave us.
      response = e?.error?.data ?? null;
    }
    if(response){
      this.user = response;
      if(error){
        this.errorToast(error);
      }
      else if(response.user_to_client?.status === UserStatus.PENDING){
        this.inviteToast();
      }
      else{
        // login instead I guess
        await this.router.navigate(['login'], {
          queryParams: {
            email: response.username,
            // with: this.id
          }
        });
        return;
      }
    }
  }

  protected listenWindow(win: Window): Promise<string>{
    return new Promise<string>((resolve, reject) => {
      if(this.wmevt){
        window.removeEventListener('message', this.wmevt, false);
        this.wmevt = null;
      }
      if(this.win && win !== this.win){
        this.win.close();
      }

      this.wmevt = (evt) => {
        if(evt.origin === location.origin) {
          if(this.win) {
            this.win.close();
          }

          if (evt.data) {
            return this.auth.bootstrap().then((login) => {
              if(this.itoast){
                this.itoast.close();
              }

              if(login){
                window.removeEventListener('message', this.wmevt, false);
                return this.router.navigateByUrl(this.defaultRoute).finally(() => {
                  return resolve(evt.data);
                });
              }
              else{
                return this.translate.get(['authfail', 'error']).toPromise().then(items => {
                  this.toast.warning(items.authfail, items.error, {
                    icon: 'person-delete'
                  });
                  return false;
                });
              }
            });
          }
        }
      };

      this.win = win;
      window.addEventListener('message', this.wmevt, false);
    });
  }

  protected inviteToast(){
    this.itoast = this.toast.success(this.translate.instant('inviteresponse'), this.translate.instant('invitefinished'), {
      duration: 15000
    });
  }

  async submitForgot() {
    if(!this.submitting && this.forgot){
      this.submitting = true;
      const form = this.forgot.getRawValue();
      let response;
      try {
        response = await this.auth.resetPassword(form.email);
      } catch {
        // Toasted by errorify (402 unknown email is the common case); stay on the form.
        return;
      } finally {
        this.submitting = false;
      }

      if(response && response.data){
        await this.notifier.notify('currentlyresetting', 'success', 'primary', 'email-outline');
      }

      await this.router.navigate(['.'], {
        queryParams: {
          email: form.email
        }
      })
    }
  }
}
